import type { ReactNode } from "react";

// Lightweight markdown renderer for post bodies. Supports section headers (##),
// sub-headers (###), numbered "idea" cards (1. **Title** — text), bullet lists,
// blockquote callouts (>), horizontal rules (---), bold and links.

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\))/;
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const match = pattern.exec(remaining);
    if (!match) { nodes.push(remaining); break; }
    if (match.index > 0) nodes.push(remaining.slice(0, match.index));
    if (match[2] !== undefined) nodes.push(<strong key={`b${key++}`}>{match[2]}</strong>);
    else nodes.push(<a key={`l${key++}`} href={match[4]} target="_blank" rel="noreferrer" className="font-medium text-navy hover:underline">{match[3]}</a>);
    remaining = remaining.slice(match.index + match[0].length);
  }
  return nodes;
}

const isHeader = (line: string) => line.startsWith("## ");
const isSubHeader = (line: string) => line.startsWith("### ");
const isQuote = (line: string) => line.startsWith(">");
const isBullet = (line: string) => line.startsWith("- ");
const isNumbered = (line: string) => /^\d+\.\s/.test(line);
const isRule = (line: string) => line === "---";
const isBlockStart = (line: string) => isHeader(line) || isSubHeader(line) || isQuote(line) || isBullet(line) || isNumbered(line) || isRule(line);

export function RichText({ markdown }: { markdown: string }) {
  const lines = markdown.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (trimmed === "") { i++; continue; }

    if (isRule(trimmed)) { blocks.push(<hr key={key++} className="my-5 border-t border-line" />); i++; continue; }

    if (isHeader(trimmed)) {
      blocks.push(
        <div key={key++} className="mb-3 mt-6 flex items-center gap-3 first:mt-0">
          <span className="mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">{trimmed.slice(3)}</span>
          <span className="h-px flex-1 bg-line" />
        </div>,
      );
      i++; continue;
    }

    if (isSubHeader(trimmed)) {
      blocks.push(<h4 key={key++} className="mb-1 mt-4 text-[15px] font-semibold text-ink">{renderInline(trimmed.slice(4))}</h4>);
      i++; continue;
    }

    if (isQuote(trimmed)) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { quoteLines.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      const paragraphs = quoteLines.join("\n").split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);
      blocks.push(
        <blockquote key={key++} className="my-4 rounded-r-lg border-l-4 border-primary bg-quote px-4 py-3">
          {paragraphs.map((paragraph, index) => <p key={index} className={`text-[15px] leading-7 text-ink ${index > 0 ? "mt-3" : ""}`}>{renderInline(paragraph)}</p>)}
        </blockquote>,
      );
      continue;
    }

    if (isNumbered(trimmed)) {
      const items: { n: string; text: string }[] = [];
      while (i < lines.length && isNumbered(lines[i].trim())) {
        const match = lines[i].trim().match(/^(\d+)\.\s+(.*)$/);
        if (match) items.push({ n: match[1], text: match[2] });
        i++;
      }
      blocks.push(
        <div key={key++} className="my-3 grid gap-2.5">
          {items.map((item, index) => {
            const titleMatch = item.text.match(/^\*\*(.+?)\*\*\s*(?:—|-)?\s*(.*)$/);
            const title = titleMatch ? titleMatch[1] : null;
            const description = titleMatch ? titleMatch[2] : item.text;
            return (
              <div key={index} className="flex gap-3 rounded-xl border border-line bg-surfaceAlt p-4">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-tint text-[13px] font-semibold text-primary">{item.n}</span>
                <div className="min-w-0">
                  {title ? <div className="text-[15px] font-semibold text-ink">{title}</div> : null}
                  {description ? <div className={`text-[14px] leading-6 text-secondary ${title ? "mt-0.5" : ""}`}>{renderInline(description)}</div> : null}
                </div>
              </div>
            );
          })}
        </div>,
      );
      continue;
    }

    if (isBullet(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) { items.push(lines[i].trim().slice(2)); i++; }
      blocks.push(
        <ul key={key++} className="my-3 grid gap-1.5">
          {items.map((item, index) => (
            <li key={index} className="flex gap-2 text-[15px] leading-6 text-ink"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /><span>{renderInline(item)}</span></li>
          ))}
        </ul>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && lines[i].trim() !== "" && !isBlockStart(lines[i].trim())) { paragraph.push(lines[i].trim()); i++; }
    blocks.push(<p key={key++} className="my-2 text-[15px] leading-7 text-ink">{renderInline(paragraph.join(" "))}</p>);
  }
  return <div>{blocks}</div>;
}

// Plain-text flattening for list previews and snippets.
export function markdownToPlain(markdown: string) {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/^[-*]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
